import React, {useEffect, useState} from 'react';

import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {exportCustomsAuditEvent, fetchCustomsAuditLogById} from 'api/customsAudit';
import {ReactComponent as DownloadIcon} from 'assets/svg/download.svg';
import {
    DetailCard,
    DetailHeader,
    DetailHeaderActions,
    DetailId,
    DetailTitle,
    EmptyDetail,
    EventBadge,
    Field,
    FieldLabel,
    FieldsGrid,
    FieldValue,
    Section,
    SectionTitle,
} from 'components/common/AuditLog/styled';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {formatLocalDateTime, translateUserRole} from 'components/pages/RecallAudit/utils';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Spinner} from 'components/ui-kit/Spinner';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {branch, CustomsAuditEventType, CustomsAuditItem} from 'types/customs-audit';
import {handleNil} from 'utils/handleNil';

import {ChangesTable} from './styled';

type DetailField =
    | 'login'
    | 'failureReason'
    | 'targetUserId'
    | 'targetUserFullName'
    | 'branchId'
    | 'branchNumber'
    | 'branchName'
    | 'branchAddress'
    | 'branchesBefore'
    | 'branchesAfter'
    | 'notificationId'
    | 'declarationNumber'
    | 'notificationParticipantOrganizationId'
    | 'oldStatus'
    | 'newStatus';

const eventFields: Record<CustomsAuditEventType, DetailField[]> = {
    AUTH_LOGIN_SUCCESS: [],
    AUTH_LOGOUT: [],
    AUTH_PASSWORD_CHANGED: [],
    AUTH_LOGIN_FAILED: ['login', 'failureReason'],
    USER_CREATED: ['targetUserId', 'targetUserFullName'],
    USER_UPDATED: ['targetUserId', 'targetUserFullName'],
    USER_BRANCHES_UPDATED: [
        'targetUserId',
        'targetUserFullName',
        'branchesBefore',
        'branchesAfter',
    ],
    BRANCH_CREATED: ['branchId', 'branchNumber', 'branchName', 'branchAddress'],
    BRANCH_UPDATED: ['branchId'],
    NOTIFICATION_VIEWED: [
        'notificationId',
        'declarationNumber',
        'notificationParticipantOrganizationId',
    ],
    NOTIFICATION_NUMBER_SET: [
        'notificationId',
        'declarationNumber',
        'notificationParticipantOrganizationId',
    ],
    NOTIFICATION_STATUS_CHANGED: ['notificationId', 'declarationNumber', 'oldStatus', 'newStatus'],
};

const WIDE_DETAIL_FIELDS = new Set<DetailField>([
    'branchesBefore',
    'branchesAfter',
    'branchAddress',
]);

const formatBranches = (branches?: branch[]) => {
    if (!branches?.length) return '';
    return branches
        .map((item) => [item.number, item.name].filter(Boolean).join(' ') || String(item.id))
        .join(', ');
};

export const CustomsAuditDetailPage = () => {
    const {id: eventId} = useParams<{id: string}>();
    const {t} = useTranslation();
    const [entry, setEntry] = useState<CustomsAuditItem | null>(null);
    const [pending, setPending] = useState(true);
    const [exportPending, setExportPending] = useState(false);

    useEffect(() => {
        let active = true;
        setEntry(null);
        setPending(true);
        fetchCustomsAuditLogById(eventId)
            .then((value) => {
                if (active) setEntry(value);
            })
            .catch((error) => {
                if (active) showRestError(error as RestError);
            })
            .finally(() => {
                if (active) setPending(false);
            });
        return () => {
            active = false;
        };
    }, [eventId]);

    const exportPdf = async () => {
        if (exportPending) return;
        setExportPending(true);
        try {
            saveAs(await exportCustomsAuditEvent(eventId), `customs-audit-${eventId}.pdf`);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setExportPending(false);
        }
    };

    const details = entry?.details;
    const changedFields = details?.changedFields ?? [];
    const detailKeys = entry ? eventFields[entry.eventType] ?? [] : [];
    const generalFields: Array<[string, React.ReactNode]> = entry
        ? [
              ['dateTime', formatLocalDateTime(entry.eventTime)],
              ['userId', handleNil(entry.userId)],
              ['userRole', translateUserRole(t, entry.userRole)],
              ['fullName', handleNil(entry.userFullName)],
              ['ipAddress', handleNil(entry.ipAddress)],
              ['branchesAtEventTime', handleNil(entry.branchesAtEventTime)],
              ['userAgent', handleNil(entry.userAgent)],
          ]
        : [];

    const renderDetailValue = (key: DetailField): React.ReactNode => {
        switch (key) {
            case 'login':
                return handleNil(details?.login);
            case 'failureReason':
                return details?.failureReason
                    ? t(`pages.customsAudit.failureReasons.${details.failureReason}`, {
                          defaultValue: details.failureReason,
                      })
                    : '—';
            case 'targetUserId':
                return handleNil(details?.targetUserId);
            case 'targetUserFullName':
                return handleNil(details?.targetUserFullName);
            case 'branchId':
                return handleNil(details?.branchId);
            case 'branchNumber':
                return handleNil(details?.branchNumber);
            case 'branchName':
                return handleNil(details?.branchName);
            case 'branchAddress':
                return handleNil(details?.branchAddress);
            case 'branchesBefore':
                return handleNil(formatBranches(details?.branchesBefore));
            case 'branchesAfter':
                return handleNil(formatBranches(details?.branchesAfter));
            case 'notificationId':
                return handleNil(entry?.notificationId);
            case 'declarationNumber':
                return handleNil(entry?.declarationNumber);
            case 'notificationParticipantOrganizationId':
                return handleNil(entry?.notificationParticipantOrganizationId);
            case 'oldStatus':
                return details?.oldStatus
                    ? t(`dictionaries.declarationStatuses.${details.oldStatus}`, {
                          defaultValue: details.oldStatus,
                      })
                    : '—';
            case 'newStatus':
                return details?.newStatus
                    ? t(`dictionaries.declarationStatuses.${details.newStatus}`, {
                          defaultValue: details.newStatus,
                      })
                    : '—';
            default:
                return '—';
        }
    };

    return (
        <Page title={t('pages.recallAudit.detail.title')}>
            <BackTo to={ROUTE.CUSTOMS_AUDIT}>{t('pages.recallAudit.detail.back')}</BackTo>
            {pending ? (
                <EmptyDetail>
                    <Spinner />
                </EmptyDetail>
            ) : !entry ? (
                <EmptyDetail>{t('pages.recallAudit.detail.notFound')}</EmptyDetail>
            ) : (
                <DetailCard>
                    <DetailHeader>
                        <div>
                            <DetailTitle>{t('pages.recallAudit.detail.title')}</DetailTitle>
                            <DetailId>ID: {entry.eventId}</DetailId>
                        </div>
                        <DetailHeaderActions>
                            <EventBadge>
                                {t(`pages.customsAudit.events.${entry.eventType}`, {
                                    defaultValue: entry.eventType,
                                })}
                            </EventBadge>
                            <PermissionAccess
                                permissions={[PERMISSION_ALIASES.customsAuditLogExport]}
                            >
                                <Button
                                    size="sm"
                                    variant={ButtonVariant.Secondary}
                                    disabled={exportPending}
                                    isLoading={exportPending}
                                    onClick={exportPdf}
                                    icon={<Icon source={DownloadIcon} width={16} height={16} />}
                                >
                                    {t('pages.recallAudit.export.pdf')}
                                </Button>
                            </PermissionAccess>
                        </DetailHeaderActions>
                    </DetailHeader>
                    <Section>
                        <SectionTitle>{t('pages.recallAudit.detail.general')}</SectionTitle>
                        <FieldsGrid>
                            {generalFields.map(([key, value]) => (
                                <Field key={key} wide={key === 'userAgent'}>
                                    <FieldLabel>
                                        {key === 'branchesAtEventTime'
                                            ? t('pages.customsAudit.fields.branchesAtEventTime')
                                            : t(`pages.recallAudit.detail.${key}`)}
                                    </FieldLabel>
                                    <FieldValue>{value}</FieldValue>
                                </Field>
                            ))}
                        </FieldsGrid>
                    </Section>
                    {!!detailKeys.length && (
                        <Section>
                            <SectionTitle>{t('pages.customsAudit.details')}</SectionTitle>
                            <FieldsGrid>
                                {detailKeys.map((key) => (
                                    <Field key={key} wide={WIDE_DETAIL_FIELDS.has(key)}>
                                        <FieldLabel>
                                            {t(`pages.customsAudit.fields.${key}`)}
                                        </FieldLabel>
                                        <FieldValue>{renderDetailValue(key)}</FieldValue>
                                    </Field>
                                ))}
                            </FieldsGrid>
                        </Section>
                    )}
                    {!!changedFields.length && (
                        <Section>
                            <SectionTitle>{t('pages.customsAudit.changes')}</SectionTitle>
                            <ChangesTable>
                                <thead>
                                    <tr>
                                        {['field', 'before', 'after'].map((key) => (
                                            <th key={key}>{t(`pages.customsAudit.${key}`)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {changedFields.map((change, index) => (
                                        <tr key={`${change.field}-${index}`}>
                                            <td>
                                                {t(
                                                    `pages.customsAudit.changedFields.${change.field}`,
                                                    {defaultValue: change.field},
                                                )}
                                            </td>
                                            <td>{handleNil(change.oldValue)}</td>
                                            <td>{handleNil(change.newValue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </ChangesTable>
                        </Section>
                    )}
                </DetailCard>
            )}
        </Page>
    );
};
